import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";

import { useAuth } from "@/providers/auth";
import { useDomainServices } from "@/providers/DomainServices";
import { useNavigation } from "@/providers/navigation/NavigationContext";
import { useCreateCollection } from "@/shared/domain/collections/hooks/useCreateCollection";
import { useUpdateCollection } from "@/shared/domain/collections/hooks/useUpdateCollection";
import { getQueryKey as searchTablePreferencesQueryKey } from "@/shared/domain/config/hooks/useSearchTablePreferences";
import { searchTablePreferenceKeyFor } from "@/shared/domain/config/searchTablePreferenceKeys";
import { eventQueryKeys } from "@/shared/domain/events/hooks";
import { usePublishToAiAgentThreadPubSubBus } from "@/shared/services/AiAgentThreadPubSubBus";
import {
  RESOURCE_TABLE_ACTION_TOPIC,
  WORKSPACE_ACTION_TOPIC,
  type ResourceTableActionPayload,
  type WorkspaceActionPayload,
} from "@/shared/services/AiAgentThreadPubSubBus/models";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import { ViewingContextService } from "@/shared/services/ViewingContextService/ViewingContextService";

import {
  AgentThreadRecord,
  AgentThreadRole,
  AgentThreadStatus,
  AgentThreadToolUseContent,
  isAgentThreadToolResultContent,
  isAgentThreadToolUseContent,
} from "../AgentThreadRecord";
import { getClientTool } from "../clientTools";
import type { ApprovalMode } from "../clientTools/approvalModes";
import {
  ClientToolBatchRunner,
  type PendingToolApproval,
} from "../clientTools/ClientToolBatchRunner";
import type { ClientToolDeps } from "../clientTools/ClientTools";
import { claimToolUses } from "../clientTools/toolUseClaims";

export type { PendingToolApproval };

/**
 * Returns the client-tool `tool_use` blocks that still need execution in a
 * thread that is in CLIENT_TOOL_TURN.
 *
 * Finds the latest assistant message (scanning from the end — NOT just
 * `messages.at(-1)`, since the server may append a `roboto` message
 * carrying partial results for server-side tool uses that ran in the same
 * turn before handing control back to the browser), collects its tool_uses,
 * and filters out any whose `tool_use_id` already has a matching
 * `tool_result` in a subsequent `roboto` message. The remaining tool_uses
 * are the ones the client still owes results for.
 *
 * Also filters out tool_uses whose `tool_name` is not a known client tool.
 * In the mixed server+client turn the session can flip to CLIENT_TOOL_TURN
 * before the backend has finished persisting every server tool_result, so
 * an unanswered server tool_use briefly looks "pending" from the browser's
 * view of the delta. Submitting a client-side error result for it would
 * land a second tool_result for the same id once the backend's real
 * tool_result arrives, and Bedrock rejects the next Converse call
 * ("The number of toolResult blocks … exceeds the number of toolUse
 * blocks"). Leave those for the backend to answer.
 *
 * Exported for unit tests.
 */
export function findPendingClientToolUses(
  agentThread: AgentThreadRecord,
  isClientTool: (toolName: string) => boolean = (name) =>
    getClientTool(name) !== undefined,
): AgentThreadToolUseContent[] {
  let assistantIdx = -1;
  for (let i = agentThread.messages.length - 1; i >= 0; i -= 1) {
    if (agentThread.messages[i].role === AgentThreadRole.ASSISTANT) {
      assistantIdx = i;
      break;
    }
  }
  if (assistantIdx === -1) {
    return [];
  }

  const toolUses = agentThread.messages[assistantIdx].content.filter(
    isAgentThreadToolUseContent,
  );
  if (toolUses.length === 0) {
    return [];
  }

  const resolvedIds = new Set<string>();
  for (let i = assistantIdx + 1; i < agentThread.messages.length; i += 1) {
    const msg = agentThread.messages[i];
    if (msg.role !== AgentThreadRole.ROBOTO) {
      continue;
    }
    for (const block of msg.content) {
      if (isAgentThreadToolResultContent(block)) {
        resolvedIds.add(block.tool_use_id);
      }
    }
  }

  return toolUses.filter(
    (tu) => !resolvedIds.has(tu.tool_use_id) && isClientTool(tu.tool_name),
  );
}

interface ClientToolExecutionOptions {
  /**
   * Stand down: watch the thread but execute nothing.
   *
   * Set when another mounted copy of the conversation owns execution for this
   * thread — the thread detail page once it has handed the thread to the chat
   * sidebar. {@link claimToolUses} already keeps a `tool_use_id` from running
   * twice; this decides which copy wins, so the batch and any approval it
   * raises land on the surface that survives the navigation.
   */
  suspended?: boolean;
}

/**
 * Hook that watches a AgentThreadRecord and handles client-side tool execution,
 * including approval gating based on per-tool approval modes.
 *
 * Delegates batch logic (triage, queue, result accumulation) to
 * {@link ClientToolBatchRunner}. This hook is a thin adapter that bridges
 * React context to the runner's lifecycle methods.
 */
export function useClientToolExecution(
  agentThread: AgentThreadRecord | undefined,
  orgId: string | undefined,
  autoEnabled: boolean,
  userPreferences: Record<string, ApprovalMode> | null | undefined,
  { suspended = false }: ClientToolExecutionOptions = {},
) {
  const {
    agentThreadService,
    agentService,
    skillService,
    events: eventService,
    topics: topicService,
    files: fileService,
    layoutService,
    collections: collectionService,
    viewService,
    configService,
  } = useDomainServices();
  const { currentUser } = useAuth();
  const { goto } = useNavigation();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { mutateAsync: createCollection } = useCreateCollection();
  const { mutateAsync: updateCollection } = useUpdateCollection();
  const publishWorkspaceAction =
    usePublishToAiAgentThreadPubSubBus<WorkspaceActionPayload>(
      WORKSPACE_ACTION_TOPIC,
    );
  const publishResourceTableAction =
    usePublishToAiAgentThreadPubSubBus<ResourceTableActionPayload>(
      RESOURCE_TABLE_ACTION_TOPIC,
    );

  const lastExecutedRef = useRef<string | null>(null);
  const [pendingApproval, setPendingApproval] =
    useState<PendingToolApproval | null>(null);

  // Deps are rebuilt each render and stored in a ref so the runner always reads
  // current values (e.g. isOnVisualizerPage reflects the user's current route).
  // The single `deps` object feeds both the ref's initial value and `current`,
  // so the closures (isOnVisualizerPage, onEventCreated) have one definition
  // rather than two copies that can drift apart.
  const deps: ClientToolDeps = {
    goto,
    orgId,
    agentService,
    skillService,
    userId: currentUser?.user_id,
    eventService,
    topicService,
    fileService,
    layoutService,
    collectionService,
    isOnVisualizerPage: () => location.pathname.startsWith("/visualize"),
    publishWorkspaceAction,
    onEventCreated: () => {
      if (!orgId) {
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.forWorkspaces(orgId),
      });
    },
    // The mutation hooks' own writes, so a tool cannot perform a collection
    // write without the invalidation that belongs to it. `collectionService`
    // above stays for reads.
    createCollection,
    updateCollection,
    viewService,
    resourceTableOnScreen: () =>
      ViewingContextService.getInstance().getViewingContext().resourceTable,
    applyViewToOpenTable: (view) =>
      publishResourceTableAction({ type: "apply_view", payload: { view } }),
    // The same write and invalidation `useUpdateSearchTablePreferences` does,
    // done by hand because that hook is per table and a tool serves all of
    // them. Only the one field, so the customer's column layout is not
    // collateral of setting a default.
    setDefaultView: async (target, viewId) => {
      if (!orgId) {
        throw new Error("Cannot set a default View with no current org");
      }
      const configKey = searchTablePreferenceKeyFor(target);
      await configService.updateTablePreferences(orgId, configKey, {
        default_view_id: viewId,
      });
      await queryClient.invalidateQueries({
        queryKey: searchTablePreferencesQueryKey(configKey),
      });
    },
  };
  const depsRef = useRef(deps);
  depsRef.current = deps;

  // Stable refs for callbacks so the runner can be created once.
  const agentThreadServiceRef = useRef(agentThreadService);
  agentThreadServiceRef.current = agentThreadService;
  const orgIdRef = useRef(orgId);
  orgIdRef.current = orgId;

  // Create runner once with stable callbacks
  const runnerRef = useRef<ClientToolBatchRunner | null>(null);
  if (!runnerRef.current) {
    runnerRef.current = new ClientToolBatchRunner({
      onPendingApprovalChanged: (pending) => setPendingApproval(pending),
      onBatchComplete: (sessionId, results) => {
        const currentOrgId = orgIdRef.current;
        if (!currentOrgId || results.length === 0) {
          return;
        }
        agentThreadServiceRef.current
          .submitToolResults(
            sessionId,
            { tool_results: results },
            { orgId: currentOrgId },
          )
          .catch((error: unknown) => {
            // Do NOT clear lastExecutedRef — tool side effects (navigation,
            // PubSub) are not idempotent and must not be re-executed.
            ErrorMonitoringService.captureError(error as Error);
          });
      },
    });
  }

  // Answer whatever this executor still owes when its surface goes away —
  // typically because a tool it just auto-executed navigated out from under
  // it. Its own effect, with an empty dependency list, because the main
  // effect's cleanup runs on every re-entry; detaching there would end a batch
  // that is merely re-rendering.
  //
  // The detach is deferred by a tick, and the effect body cancels it. A real
  // unmount never re-runs the effect, so the detach lands a moment later and
  // nothing observable changes. StrictMode is the reason for the tick: a
  // development build runs this cleanup once during the initial mount, after
  // the main effect above has already queued a batch, and then re-runs the
  // effect synchronously. A synchronous detach there strands whatever that
  // batch queued and marks the runner, which outlives the simulated unmount
  // in its ref, detached for the rest of the session — every approval-gated
  // tool then fails the instant it is triaged, and only on the dev server.
  const detachTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (detachTimerRef.current !== null) {
      clearTimeout(detachTimerRef.current);
      detachTimerRef.current = null;
    }
    return () => {
      detachTimerRef.current = setTimeout(() => {
        detachTimerRef.current = null;
        runnerRef.current?.detach();
      }, 0);
    };
  }, []);

  // Main effect: detect CLIENT_TOOL_TURN and delegate to runner
  useEffect(() => {
    if (!agentThread || !orgId || suspended) {
      return;
    }
    if (agentThread.status !== AgentThreadStatus.CLIENT_TOOL_TURN) {
      return;
    }

    const pending = findPendingClientToolUses(agentThread);
    if (pending.length === 0) {
      return;
    }

    // Deduplicate on the pending tool_use_ids, not on message count: the
    // server appends a `roboto` message mid-turn carrying partial server-tool
    // results, and a count-keyed key reads that as a new batch. The runner
    // ignores ids it has already handled, so a re-entry is harmless either
    // way; this just skips the call.
    const pendingIds = pending.map((toolUse) => toolUse.tool_use_id).join(",");
    const executionKey = `${agentThread.thread_id}:${pendingIds}`;
    if (lastExecutedRef.current === executionKey) {
      return;
    }

    // Claim before running: another mounted copy of this conversation derives
    // "pending" from its own fetch and would otherwise execute the same
    // tool_use — two panels opened, two results submitted for one id. The
    // ordering is load-bearing. Claiming after the `lastExecutedRef` check
    // keeps a re-entry by this instance cheap, and recording the key only once
    // we hold work leaves this instance free to take a later batch when it
    // loses the race.
    const claimed = new Set(
      claimToolUses(
        agentThread.thread_id,
        pending.map((toolUse) => toolUse.tool_use_id),
      ),
    );
    if (claimed.size === 0) {
      return;
    }

    lastExecutedRef.current = executionKey;

    void runnerRef.current!.startBatch({
      sessionId: agentThread.thread_id,
      toolUses: pending.filter((toolUse) => claimed.has(toolUse.tool_use_id)),
      autoEnabled,
      userPreferences,
      deps: depsRef.current,
      resolveClientTool: getClientTool,
    });
  }, [agentThread, orgId, autoEnabled, userPreferences, suspended]);

  const handleApprove = useCallback(async () => {
    await runnerRef.current?.approve(depsRef.current);
  }, []);

  const handleDecline = useCallback(() => {
    runnerRef.current?.decline();
  }, []);

  return {
    pendingApproval,
    handleApprove,
    handleDecline,
  };
}
